import { useEffect, useMemo, useState } from 'react';
import type {
  FuncionEquipo,
  PropiedadResumen,
  Rol,
  UsuarioEquipo,
} from '@matias-asset-os/contracts';
import { FUNCIONES_EQUIPO } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Modal } from '../componentes/Modal.js';
import { Icono } from '../componentes/Iconos.js';

/**
 * GESTIÓN DE USUARIOS
 *
 * Sustituye a «Personas y empresas». Desde aquí se da de alta a alguien, se le
 * cambia el idioma, la función o el rol, se le restablece la contraseña y se
 * decide a qué propiedades llega.
 *
 * La pantalla insiste en una distinción que el modelo hace y que es fácil
 * confundir al mirarla: el ROL es el permiso (a qué datos llega) y la FUNCIÓN
 * es el oficio (a qué se dedica). Un jardinero es, en permisos, un manager de
 * las casas que atiende. Por eso los dos campos van separados y cada uno lleva
 * su explicación.
 *
 * Y una regla que se enseña en vez de esconderse: a quien ya registró trabajo
 * no se le ofrece «eliminar», sólo «ocultar», porque borrarlo dejaría sus horas
 * sin dueño y movería el coste del mes.
 */

const IDIOMAS: Array<{ clave: 'es' | 'en' | 'de'; texto: string }> = [
  { clave: 'es', texto: 'Español' },
  { clave: 'en', texto: 'English' },
  { clave: 'de', texto: 'Deutsch' },
];

/*
  CAPTADOR va el ultimo porque es el que menos se crea, no porque valga menos.
  Sale a la calle: entra al CRM, registra puertas y obras, y no ve nada mas.
*/
const ROLES: Rol[] = ['DIRECTOR', 'MANAGER', 'CLIENTE', 'PROVEEDOR', 'CAPTADOR', 'PROMOTOR'];

type Filtro = 'TODOS' | 'EQUIPO' | 'CLIENTES' | 'OCULTOS';

function iniciales(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function Usuarios({ idioma, yoId }: { idioma: Idioma; yoId: string }) {
  const [equipo, setEquipo] = useState<UsuarioEquipo[] | null>(null);
  const [propiedades, setPropiedades] = useState<PropiedadResumen[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('TODOS');
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);

  const cargar = () => {
    api
      .equipo()
      .then((r) => setEquipo(r.equipo))
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      );
  };

  useEffect(() => {
    cargar();
    api
      .propiedades()
      .then((r) => setPropiedades(r.propiedades))
      .catch(() => undefined);
  }, [idioma]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (equipo ?? [])
      .filter((u) => {
        if (filtro === 'OCULTOS') return !u.activo;
        if (!u.activo) return false;
        if (filtro === 'EQUIPO') return u.rol === 'DIRECTOR' || u.rol === 'MANAGER';
        if (filtro === 'CLIENTES') return u.rol === 'CLIENTE';
        return true;
      })
      .filter((u) => !q || u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [equipo, filtro, busqueda]);

  const cuenta = (f: Filtro) =>
    (equipo ?? []).filter((u) => {
      if (f === 'OCULTOS') return !u.activo;
      if (!u.activo) return false;
      if (f === 'EQUIPO') return u.rol === 'DIRECTOR' || u.rol === 'MANAGER';
      if (f === 'CLIENTES') return u.rol === 'CLIENTE';
      return true;
    }).length;

  const seleccionado = (equipo ?? []).find((u) => u.id === abierto) ?? null;

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'equipo.titulo')}</h1>
          <p>{t(idioma, 'equipo.resumen', { total: (equipo ?? []).length })}</p>
        </div>
        <div className="acciones">
          <button type="button" onClick={() => setCreando(true)}>
            ＋ {t(idioma, 'equipo.nuevo')}
          </button>
        </div>
      </div>

      {error && (
        <p className="aviso-demo" role="alert">
          {error}
        </p>
      )}

      <div className="pestanas" role="tablist">
        {(['TODOS', 'EQUIPO', 'CLIENTES', 'OCULTOS'] as Filtro[]).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filtro === f}
            onClick={() => setFiltro(f)}
          >
            {t(idioma, `equipo.filtro${f}`)} <span className="chip-area">{cuenta(f)}</span>
          </button>
        ))}
      </div>

      <div className="campo">
        <label htmlFor="buscar-usuario" className="oculto-visual">
          {t(idioma, 'equipo.buscar')}
        </label>
        <input
          id="buscar-usuario"
          type="search"
          value={busqueda}
          placeholder={t(idioma, 'equipo.buscar')}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      {equipo === null && !error && <p className="suave">{t(idioma, 'app.cargando')}</p>}

      <div className="rejilla usuarios">
        {visibles.map((u) => (
          <button key={u.id} type="button" className="tarjeta-persona" onClick={() => setAbierto(u.id)}>
            <span className="avatar" aria-hidden="true">
              {iniciales(u.nombre)}
            </span>
            <span className="tarjeta-persona-texto">
              <b>{u.nombre}</b>
              <small>{u.email}</small>
              <span className="tarjeta-persona-chips">
                <span className={`pastilla ${u.rol === 'DIRECTOR' ? 'ambar' : 'neutra'}`}>
                  {t(idioma, `rol.${u.rol}`)}
                </span>
                {u.funcion && (
                  <span className="chip-area">{t(idioma, `funcion.${u.funcion}`)}</span>
                )}
                {u.propiedades.length > 0 && (
                  <span className="chip-area">
                    {t(idioma, 'equipo.nProp', { total: u.propiedades.length })}
                  </span>
                )}
                {!u.activo && <span className="pastilla rojo">{t(idioma, 'equipo.oculto')}</span>}
              </span>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        ))}
        {equipo !== null && visibles.length === 0 && (
          <p className="suave">{t(idioma, 'equipo.sinResultados')}</p>
        )}
      </div>

      {creando && (
        <ModalNuevoUsuario
          idioma={idioma}
          onHecho={cargar}
          onCerrar={() => setCreando(false)}
        />
      )}

      {seleccionado && (
        <PanelUsuario
          idioma={idioma}
          usuario={seleccionado}
          esYo={seleccionado.id === yoId}
          propiedades={propiedades}
          onCambio={cargar}
          onCerrar={() => setAbierto(null)}
        />
      )}
    </main>
  );
}

// --- Alta --------------------------------------------------------------------

function ModalNuevoUsuario({
  idioma,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rol, setRol] = useState<Rol>('MANAGER');
  const [funcion, setFuncion] = useState<FuncionEquipo>('MANAGER');
  const [lang, setLang] = useState<'es' | 'en' | 'de'>('es');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'equipo.nuevo')} onCerrar={onCerrar}>
      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(e) => {
          e.preventDefault();
          setEnviando(true);
          setError(null);
          api
            .crearUsuario({ nombre: nombre.trim(), email: email.trim(), password, rol, funcion, idioma: lang })
            .then(() => {
              onHecho();
              onCerrar();
            })
            .catch((f: unknown) =>
              setError(
                f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor'),
              ),
            )
            .finally(() => setEnviando(false));
        }}
      >
        <div className="campo">
          <label htmlFor="nu-nombre">{t(idioma, 'equipo.nombreCompleto')}</label>
          <input
            id="nu-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder={t(idioma, 'equipo.nombrePista')}
            required
            minLength={2}
          />
        </div>
        <div className="campo">
          <label htmlFor="nu-email">{t(idioma, 'auth.email')}</label>
          <input
            id="nu-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="campo">
          <label htmlFor="nu-clave">{t(idioma, 'equipo.claveInicial')}</label>
          <input
            id="nu-clave"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t(idioma, 'equipo.claveMinimo')}
            required
            minLength={8}
          />
        </div>

        <SelectorRol idioma={idioma} valor={rol} onCambio={setRol} />
        <SelectorFuncion idioma={idioma} valor={funcion} onCambio={setFuncion} />

        <fieldset className="grupo-opciones">
          <legend>{t(idioma, 'equipo.idioma')}</legend>
          <div className="opciones-linea">
            {IDIOMAS.map((i) => (
              <button
                key={i.clave}
                type="button"
                aria-pressed={lang === i.clave}
                onClick={() => setLang(i.clave)}
              >
                {i.texto}
              </button>
            ))}
          </div>
        </fieldset>

        {error && (
          <p className="aviso-demo" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="acceso-entrar" disabled={enviando}>
          ＋ {t(idioma, 'equipo.crear')}
        </button>
      </form>
    </Modal>
  );
}

function SelectorRol({
  idioma,
  valor,
  onCambio,
}: {
  idioma: Idioma;
  valor: Rol;
  onCambio: (r: Rol) => void;
}) {
  return (
    <fieldset className="grupo-opciones">
      <legend>{t(idioma, 'equipo.rolSistema')}</legend>
      <p className="suave" style={{ fontSize: '0.8125rem', margin: '0 0 0.5rem' }}>
        {t(idioma, 'equipo.rolAyuda')}
      </p>
      <div className="opciones-linea">
        {ROLES.map((r) => (
          <button key={r} type="button" aria-pressed={valor === r} onClick={() => onCambio(r)}>
            {t(idioma, `rol.${r}`)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function SelectorFuncion({
  idioma,
  valor,
  onCambio,
}: {
  idioma: Idioma;
  valor: FuncionEquipo | null;
  onCambio: (f: FuncionEquipo) => void;
}) {
  return (
    <fieldset className="grupo-opciones">
      <legend>{t(idioma, 'equipo.funcion')}</legend>
      <p className="suave" style={{ fontSize: '0.8125rem', margin: '0 0 0.5rem' }}>
        {t(idioma, 'equipo.funcionAyuda')}
      </p>
      <div className="opciones-linea">
        {FUNCIONES_EQUIPO.map((f) => (
          <button key={f} type="button" aria-pressed={valor === f} onClick={() => onCambio(f)}>
            {t(idioma, `funcion.${f}`)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

// --- Ficha -------------------------------------------------------------------

function PanelUsuario({
  idioma,
  usuario,
  esYo,
  propiedades,
  onCambio,
  onCerrar,
}: {
  idioma: Idioma;
  usuario: UsuarioEquipo;
  esYo: boolean;
  propiedades: PropiedadResumen[];
  onCambio: () => void;
  onCerrar: () => void;
}) {
  const [pestana, setPestana] = useState<'info' | 'props'>('info');
  const [nombre, setNombre] = useState(usuario.nombre);
  const [email, setEmail] = useState(usuario.email);
  const [clave, setClave] = useState('');
  const [busca, setBusca] = useState('');
  const [rolAsig, setRolAsig] = useState<'PRINCIPAL' | 'SECUNDARIO'>('PRINCIPAL');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const fallo = (f: unknown) =>
    setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor'));

  const hacer = (p: Promise<unknown>, mensaje?: string) => {
    setError(null);
    void p
      .then(() => {
        if (mensaje) setAviso(mensaje);
        onCambio();
      })
      .catch(fallo);
  };

  const yaTiene = new Set(usuario.propiedades.map((p) => p.propiedadId));
  const candidatas = propiedades
    .filter((p) => !yaTiene.has(p.id))
    .filter((p) => {
      const q = busca.trim().toLowerCase();
      return !q || p.nombre.toLowerCase().includes(q) || p.codigo.toLowerCase().includes(q);
    })
    .slice(0, 8);

  return (
    <Modal idioma={idioma} titulo={usuario.nombre} onCerrar={onCerrar}>
      <div className="fila-flex entre" style={{ marginBottom: '0.5rem' }}>
        <span className={`pastilla ${usuario.activo ? 'verde' : 'rojo'}`}>
          {t(idioma, usuario.activo ? 'equipo.activo' : 'equipo.oculto')}
        </span>
        <span className="chip-area">{t(idioma, `rol.${usuario.rol}`)}</span>
      </div>

      <div className="pestanas" role="tablist">
        <button type="button" role="tab" aria-selected={pestana === 'info'} onClick={() => setPestana('info')}>
          {t(idioma, 'equipo.informacion')}
        </button>
        <button type="button" role="tab" aria-selected={pestana === 'props'} onClick={() => setPestana('props')}>
          {t(idioma, 'equipo.propiedades')} ({usuario.propiedades.length})
        </button>
      </div>

      {error && (
        <p className="aviso-demo" role="alert">
          {error}
        </p>
      )}
      {aviso && (
        <p className="pastilla verde" role="status">
          {aviso}
        </p>
      )}

      {pestana === 'info' && (
        <div className="formulario" style={{ maxWidth: 'none' }}>
          <div className="campo">
            <label htmlFor="ed-nombre">{t(idioma, 'equipo.nombreCompleto')}</label>
            <input id="ed-nombre" type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="ed-email">{t(idioma, 'auth.email')}</label>
            <input id="ed-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <fieldset className="grupo-opciones">
            <legend>{t(idioma, 'equipo.idioma')}</legend>
            <div className="opciones-linea">
              {IDIOMAS.map((i) => (
                <button
                  key={i.clave}
                  type="button"
                  aria-pressed={usuario.idioma === i.clave}
                  onClick={() => hacer(api.editarUsuario(usuario.id, { idioma: i.clave }))}
                >
                  {i.texto}
                </button>
              ))}
            </div>
          </fieldset>

          <button
            type="button"
            className="acceso-entrar"
            onClick={() =>
              hacer(
                api.editarUsuario(usuario.id, { nombre: nombre.trim(), email: email.trim() }),
                t(idioma, 'equipo.guardado'),
              )
            }
          >
            ✓ {t(idioma, 'equipo.guardarCambios')}
          </button>

          <fieldset className="grupo-opciones">
            <legend>{t(idioma, 'equipo.rolSistema')}</legend>
            <p className="suave" style={{ fontSize: '0.8125rem', margin: '0 0 0.5rem' }}>
              {t(idioma, 'equipo.rolAyuda')}
            </p>
            <div className="opciones-linea">
              {ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  aria-pressed={usuario.rol === r}
                  onClick={() => hacer(api.editarUsuario(usuario.id, { rol: r }))}
                >
                  {t(idioma, `rol.${r}`)}
                </button>
              ))}
            </div>
            <p className="suave" style={{ fontSize: '0.8125rem' }}>
              {t(idioma, 'equipo.rolInmediato')}
            </p>
          </fieldset>

          {/*
            EL ACCESO AL CRM SE DA Y SE QUITA AQUI, PERSONA A PERSONA.

            El embudo ensena con quien se esta hablando, por cuanto y en que
            punto: no le corresponde a todo el equipo por tener cuenta. A la
            direccion no se le ofrece el interruptor porque entra siempre, y un
            producto donde el administrador puede dejarse fuera de su propia
            herramienta garantiza una llamada de telefono.
          */}
          {usuario.rol !== 'DIRECTOR' && (
            <fieldset className="grupo-opciones">
              <legend>{t(idioma, 'equipo.accesoCrm')}</legend>
              <p className="suave" style={{ fontSize: '0.8125rem', margin: '0 0 0.5rem' }}>
                {t(idioma, 'equipo.accesoCrmAyuda')}
              </p>
              <div className="opciones-linea">
                <button
                  type="button"
                  aria-pressed={usuario.crmHabilitado}
                  onClick={() => hacer(api.editarUsuario(usuario.id, { crmHabilitado: true }))}
                >
                  {t(idioma, 'equipo.crmSi')}
                </button>
                <button
                  type="button"
                  aria-pressed={!usuario.crmHabilitado}
                  onClick={() => hacer(api.editarUsuario(usuario.id, { crmHabilitado: false }))}
                >
                  {t(idioma, 'equipo.crmNo')}
                </button>
              </div>
            </fieldset>
          )}

          <fieldset className="grupo-opciones">
            <legend>{t(idioma, 'equipo.funcion')}</legend>
            <p className="suave" style={{ fontSize: '0.8125rem', margin: '0 0 0.5rem' }}>
              {t(idioma, 'equipo.funcionAyuda')}
            </p>
            <div className="opciones-linea">
              {FUNCIONES_EQUIPO.map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={usuario.funcion === f}
                  onClick={() => hacer(api.editarUsuario(usuario.id, { funcion: f }))}
                >
                  {t(idioma, `funcion.${f}`)}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="grupo-opciones">
            <legend>{t(idioma, 'equipo.restablecerClave')}</legend>
            <div className="fila-flex" style={{ gap: '0.5rem', alignItems: 'flex-end' }}>
              <div className="campo crecer">
                <label htmlFor="ed-clave" className="oculto-visual">
                  {t(idioma, 'equipo.claveNueva')}
                </label>
                <input
                  id="ed-clave"
                  type="password"
                  value={clave}
                  minLength={8}
                  placeholder={t(idioma, 'equipo.claveNueva')}
                  onChange={(e) => setClave(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="secundario"
                disabled={clave.length < 8}
                onClick={() =>
                  hacer(
                    api.restablecerPassword(usuario.id, clave).then(() => setClave('')),
                    t(idioma, 'equipo.claveCambiada'),
                  )
                }
              >
                {t(idioma, 'equipo.guardar')}
              </button>
            </div>
            <p className="suave" style={{ fontSize: '0.8125rem' }}>
              {t(idioma, 'equipo.claveCierraSesiones')}
            </p>
          </fieldset>

          {/* Ocultar o eliminar: la pantalla dice cuál procede y por qué. */}
          {!esYo && (
            <fieldset className="grupo-opciones peligro">
              <legend>
                {t(idioma, usuario.tieneHistorico ? 'equipo.ocultarUsuario' : 'equipo.eliminarUsuario')}
              </legend>
              <p className="suave" style={{ fontSize: '0.8125rem' }}>
                {t(idioma, usuario.tieneHistorico ? 'equipo.tieneHistoricoNota' : 'equipo.sinHistoricoNota')}
              </p>
              {usuario.activo ? (
                usuario.tieneHistorico ? (
                  <button
                    type="button"
                    className="secundario"
                    onClick={() => hacer(api.ocultarUsuario(usuario.id))}
                  >
                    {t(idioma, 'equipo.ocultarUsuario')}
                  </button>
                ) : (
                  <div className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="secundario"
                      onClick={() => hacer(api.ocultarUsuario(usuario.id))}
                    >
                      {t(idioma, 'equipo.ocultarUsuario')}
                    </button>
                    <button
                      type="button"
                      className="secundario"
                      onClick={() =>
                        hacer(api.eliminarUsuario(usuario.id).then(onCerrar))
                      }
                    >
                      <Icono nombre="papelera" /> {t(idioma, 'equipo.eliminarUsuario')}
                    </button>
                  </div>
                )
              ) : (
                <button
                  type="button"
                  onClick={() => hacer(api.reactivarUsuario(usuario.id))}
                >
                  {t(idioma, 'equipo.reactivar')}
                </button>
              )}
            </fieldset>
          )}
        </div>
      )}

      {pestana === 'props' && (
        <div className="formulario" style={{ maxWidth: 'none' }}>
          {usuario.propiedades.length === 0 ? (
            <p className="suave">{t(idioma, 'equipo.sinPropiedades')}</p>
          ) : (
            <div className="lista">
              {usuario.propiedades.map((p) => (
                <div key={p.propiedadId} className="fila">
                  <span aria-hidden="true">⌂</span>
                  <span className="crecer">
                    <b>{p.nombre}</b>
                    <small>{p.codigo}</small>
                  </span>
                  <span className="chip-area">{t(idioma, `asignacion.${p.rol}`)}</span>
                  <button
                    type="button"
                    className="enlace"
                    aria-label={t(idioma, 'equipo.retirar')}
                    onClick={() => hacer(api.retirarPropiedadDeUsuario(usuario.id, p.propiedadId))}
                  >
                    <Icono nombre="papelera" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <>
              <div className="campo">
                <label htmlFor="asig-buscar">
                  {t(idioma, usuario.rol === 'CLIENTE' ? 'equipo.vincularPropiedad' : 'equipo.asignarPropiedad')}
                </label>
                <input
                  id="asig-buscar"
                  type="search"
                  value={busca}
                  placeholder={t(idioma, 'equipo.buscarPropiedad')}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
              <div className="lista">
                {candidatas.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="fila"
                    onClick={() =>
                      hacer(
                        api
                          .asignarPropiedadAUsuario(usuario.id, {
                            propiedadId: p.id,
                            rol: rolAsig,
                          })
                          .then(() => setBusca('')),
                      )
                    }
                  >
                    <span aria-hidden="true">⌂</span>
                    <span className="crecer">{p.nombre}</span>
                    <span className="chip-area">{p.codigo}</span>
                  </button>
                ))}
                {candidatas.length === 0 && (
                  <p className="suave">{t(idioma, 'equipo.sinCandidatas')}</p>
                )}
              </div>
              <fieldset className="grupo-opciones">
                <legend>{t(idioma, 'equipo.rolEnPropiedad')}</legend>
                <div className="opciones-linea">
                  {(['PRINCIPAL', 'SECUNDARIO'] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      aria-pressed={rolAsig === r}
                      onClick={() => setRolAsig(r)}
                    >
                      {/*
                        Para un propietario esto no es «principal o de apoyo»
                        sino «dueno o contacto autorizado»: son dos vinculos
                        distintos y conviene llamarlos por su nombre.
                      */}
                      {t(
                        idioma,
                        usuario.rol === 'CLIENTE'
                          ? r === 'PRINCIPAL'
                            ? 'asignacion.OWNER'
                            : 'asignacion.AUTHORIZED_CONTACT'
                          : `asignacion.${r}`,
                      )}
                    </button>
                  ))}
                </div>
              </fieldset>
            </>
        </div>
      )}
    </Modal>
  );
}
